import { CSSProperties, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Plus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { connectorsPath } from '../../../lib/connectors-route';
import { sortConnectors, useAppsController, type AppsController } from '../../../lib/use-connectors';
import { connectorDescriptionKey, connectorDisplayName, type ConnectorStatus } from '../../../lib/connectors';
import { homeConnectorGroups } from '../../../lib/home-connector-groups';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Button, Card, IconButton } from '../ui';
import styles from './Home2ConnectorsSection.module.css';

// Home 2 — the full-width Connectors section (Figma 1859:3167), below the bento rows.
// Two sub-sections over the shared connectors controller (useAppsController):
//   • Recommended — the top few not-yet-connected connectors in the curated most-popular order
//     (sortConnectors). No "business connector kit" source exists in orion-web, so this is the catalog
//     order — matching the Figma annotation.
//   • Connected — the account's connected connectors, each with a manage "+" (add another account).
// Connect / + trigger the real OAuth flow (apps.connect / apps.addAnother). Hidden when connectors are
// disabled for the account or there is nothing to show.

const RECOMMENDED_COUNT = 3;
const CONNECTED_COUNT = 3;

const SECTION: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
};
const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};
const SUBSECTION: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, width: '100%' };
const LABEL: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 12,
  fontWeight: 'var(--sym-weight-medium)',
  color: 'var(--ds-text-secondary)',
  textTransform: 'uppercase',
};
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0 };

const PILL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  padding: '8px 12px',
  borderRadius: 8,
  background: 'var(--ds-surface-standard-bg)',
};
// Logo + info as one group, TOP-aligned so the logo lines up with the connector name (not centered on
// the whole name+subtitle block). The action button stays centered by living outside this group.
const MAIN: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, flex: '1 1 auto', minWidth: 0 };
const INFO: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 };
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const SUBTITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 11,
  color: 'var(--ds-text-secondary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const SUBTITLE_ACTIVE: CSSProperties = { ...SUBTITLE, color: 'var(--ds-text-accent)' };

/**
 * One connector row. Also the Wins page's recommended connectors (`standalone`: each row its own white
 * card on the page's grey, where Home nests them in one card), so the two pages cannot draw a connector
 * two ways.
 */
export function ConnectorRow({
  connector,
  controller,
  variant,
  standalone = false,
  onConnect,
}: {
  connector: ConnectorStatus;
  controller: AppsController;
  variant: 'recommended' | 'connected';
  standalone?: boolean;
  /** Called before the connect starts, for the host page's own click event. */
  onConnect?: (connector: ConnectorStatus) => void;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const name = connectorDisplayName(connector);

  // Mirrors the (non-exported) useConnectorDescription in ConnectorCard: prefer the localized
  // connectors.desc.<slug> key, else the server blurb.
  const key = connectorDescriptionKey(connector);
  const localized = key ? t(key, { productName }) : '';
  const description =
    variant === 'connected'
      ? t('home2.connectors.connectedActive')
      : localized && localized !== key
        ? localized
        : connector.description || '';

  const body = (
    <>
      <div style={MAIN}>
        <ConnectorLogo connector={connector} name={name} size={24} radius={6} />
        <span style={INFO}>
          <span style={NAME} title={name}>
            {name}
          </span>
          {/* The description is truncated to one line; the full text is on hover via the native title. */}
          <span style={variant === 'connected' ? SUBTITLE_ACTIVE : SUBTITLE} title={description || undefined}>
            {description}
          </span>
        </span>
      </div>
      {variant === 'recommended' ? (
        <Button
          size="medium"
          onClick={() => {
            onConnect?.(connector);
            void controller.connect(connector);
          }}
          loading={controller.busy.has(connector.toolkit)}
        >
          {t('common.connect')}
        </Button>
      ) : (
        <IconButton
          variant="text"
          aria-label={t('home2.connectors.addAccount', { name })}
          onClick={() => controller.addAnother(connector)}
        >
          <Plus size={16} aria-hidden />
        </IconButton>
      )}
    </>
  );

  return standalone ? (
    <Card size="sm" className={styles.standaloneRow}>
      {body}
    </Card>
  ) : (
    <div style={PILL}>{body}</div>
  );
}

export function Home2ConnectorsSection() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const router = useRouter();
  const controller = useAppsController();
  const alfredOnOutreach = useAlfredInOutreachEnabled();

  // The home is kept alive by DashboardHost (never remounts), and connectors are local state with no
  // shared cache — so a disconnect/connect done on the connectors screen wouldn't otherwise reach this
  // section. Re-read whenever the user navigates BACK to the home route so it re-syncs.
  const refreshRef = useRef(controller.refresh);
  refreshRef.current = controller.refresh;
  const wasHomeRef = useRef(router.pathname === '/');
  useEffect(() => {
    const isHome = router.pathname === '/';
    if (isHome && !wasHomeRef.current) void refreshRef.current();
    wasHomeRef.current = isHome;
  }, [router.pathname]);

  const state = controller.state;
  if (state.kind !== 'ready' || !state.enabled) return null;

  const groups = homeConnectorGroups(state.connectors, alfredOnOutreach);
  const recommended = sortConnectors(groups.recommended).slice(0, RECOMMENDED_COUNT);
  const connected = sortConnectors(groups.connected).slice(0, CONNECTED_COUNT);
  if (recommended.length === 0 && connected.length === 0) return null;

  return (
    <div style={SECTION}>
      <div style={HEADER}>
        <span style={TITLE}>{t('home2.connectors.title')}</span>
        {/* BARE path, no `basePath` — the DS Button resolves an href against the active mount itself
            (`as={linkAs ?? basePath + href}`), so prefixing it here produced
            `/orion-web/orion-web/settings/connectors/tools` and a route Next has never heard of. */}
        <Button
          size="medium"
          href={connectorsPath({ tab: 'tools' })}
          onClick={() => logPageClickBi({ page_name: 'home', button_name: 'manage_connectors' })}
        >
          {t('home2.connectors.manage')}
        </Button>
      </div>

      <Card style={CARD}>
        {recommended.length > 0 && (
          <div style={SUBSECTION}>
            <span style={LABEL}>{t('home2.connectors.recommended')}</span>
            <div className={styles.list}>
              {recommended.map((c) => (
                <ConnectorRow key={c.toolkit} connector={c} controller={controller} variant="recommended" />
              ))}
            </div>
          </div>
        )}

        {recommended.length > 0 && connected.length > 0 && <div style={DIVIDER} />}

        {connected.length > 0 && (
          <div style={SUBSECTION}>
            <span style={LABEL}>{t('home2.connectors.connected')}</span>
            <div className={styles.list}>
              {connected.map((c) => (
                <ConnectorRow key={c.toolkit} connector={c} controller={controller} variant="connected" />
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
